import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CategoryManager = ({ books, onBulkCategoryUpdate, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [selectedBookIds, setSelectedBookIds] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [message, setMessage] = useState(null);

  // Derive categories from existing books
  const existingCategories = [...new Set(books?.map((b) => b?.category)?.filter(Boolean))]?.sort();

  const categoryCounts = existingCategories?.reduce((acc, cat) => {
    acc[cat] = books?.filter((b) => b?.category === cat)?.length;
    return acc;
  }, {});

  const handleSelectBook = (id, checked) => {
    setSelectedBookIds((prev) =>
      checked ? [...prev, id] : prev?.filter((bid) => bid !== id)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedBookIds(checked ? books?.map((b) => b?.id) : []);
  };

  const handleApplyCategory = async () => {
    const targetCategory = newCategory?.trim() || selectedCategory;
    if (!targetCategory || selectedBookIds?.length === 0) {
      setMessage({ type: 'error', text: 'Select books and a target category' });
      return;
    }
    setIsUpdating(true);
    try {
      await onBulkCategoryUpdate?.(selectedBookIds, targetCategory);
      setMessage({ type: 'success', text: `Updated ${selectedBookIds?.length} books to category "${targetCategory}"` });
      setSelectedBookIds([]);
      setNewCategory('');
    } catch (err) {
      setMessage({ type: 'error', text: err?.message || 'Update failed' });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Category Manager</h2>
            <p className="text-sm text-muted-foreground mt-1">Organize books by category</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-muted transition-colors" aria-label="Close">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Category Overview */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">Current Categories</h3>
            <div className="flex flex-wrap gap-2">
              {existingCategories?.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition-colors ${
                    selectedCategory === cat
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-primary/10'
                  }`}
                >
                  <span className="capitalize">{cat}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    selectedCategory === cat ? 'bg-white/20' : 'bg-background'
                  }`}>
                    {categoryCounts?.[cat]}
                  </span>
                </button>
              ))}
              {existingCategories?.length === 0 && (
                <p className="text-sm text-muted-foreground">No categories found</p>
              )}
            </div>
          </div>

          {/* Bulk Reassign */}
          <div className="border border-border rounded-lg p-4 space-y-4">
            <h3 className="text-sm font-medium text-foreground">Bulk Category Reassignment</h3>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Assign to existing category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => { setSelectedCategory(e?.target?.value); setNewCategory(''); }}
                  className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="">Select category...</option>
                  {existingCategories?.map((cat) => (
                    <option key={cat} value={cat}>{cat?.charAt(0)?.toUpperCase() + cat?.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Or create new category</label>
                <Input
                  value={newCategory}
                  onChange={(e) => { setNewCategory(e?.target?.value); setSelectedCategory(''); }}
                  placeholder="New category name"
                />
              </div>
            </div>

            {/* Book Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-muted-foreground">Select books to reassign ({selectedBookIds?.length} selected)</label>
                <button
                  onClick={() => handleSelectAll(selectedBookIds?.length !== books?.length)}
                  className="text-xs text-primary hover:underline"
                >
                  {selectedBookIds?.length === books?.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                {books?.map((book) => (
                  <label key={book?.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedBookIds?.includes(book?.id)}
                      onChange={(e) => handleSelectBook(book?.id, e?.target?.checked)}
                      className="rounded border-border"
                    />
                    <span className="text-sm text-foreground flex-1 truncate">{book?.title}</span>
                    <span className="text-xs text-muted-foreground capitalize shrink-0">{book?.category}</span>
                  </label>
                ))}
              </div>
            </div>

            {message && (
              <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
                message?.type === 'success' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
              }`}>
                <Icon name={message?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
                {message?.text}
              </div>
            )}

            <Button
              onClick={handleApplyCategory}
              disabled={isUpdating || selectedBookIds?.length === 0 || (!selectedCategory && !newCategory?.trim())}
              className="w-full"
            >
              {isUpdating ? (
                <span className="flex items-center gap-2">
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  Updating...
                </span>
              ) : (
                `Apply Category to ${selectedBookIds?.length} Books`
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryManager;
